import { BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostBinding, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BaseballScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    BaseballStatistics,
    EventModel,
    EventScoreboard,
    OrderType,
    ParticipantSetScore,
    SetGameScore,
} from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { sumBy, times, values } from 'lodash-es';
import { filter } from 'rxjs';

import { BaseballScoreboardBaseRunnerComponent } from './baseball-scoreboard-base-runner.component';
import { ScoreCardComponent } from './score-card.component';
import { ScoreboardParticipantComponent } from './scoreboard-participant.component';

@Component({
    selector: 'ms-baseball-scoreboard-live',
    templateUrl: 'baseball-scoreboard-live.component.html',
    imports: [
        CommonModule,
        BaseballScoreboardBaseRunnerComponent,
        ScoreboardParticipantComponent,
        ScoreCardComponent,
        IconCustomComponent,
        DsButtonIcon,
    ],
})
export class BaseballScoreboardLiveComponent implements OnChanges {
    @HostBinding('class') className = 'baseball-live';
    @Input() scoreboard: EventScoreboard;
    @Input() event: EventModel;
    @Input() isShowcase = false;
    @Input() isNewEDPScoreboard = false;
    @Input() eventSuspended = false;
    startingIndex = 0;
    endIndex = 0;
    mobileMaxInnings = 9;
    isLandscape = false;
    isMobile: boolean;
    desktopMaxInnings = 20;
    defaultInnings = 9;
    player1runs = 0;
    player2runs = 0;
    homeTeamBase?: string;
    awayTeamBase?: string;

    private readonly maximumInningsInMobile = 9;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private mediaService: MediaQueryService,
        public sitecore: Sitecore,
        private baseballScoreboardFactory: BaseballScoreboardFactory,
    ) {
        this.init();
    }

    private init(): void {
        this.setMediaSize();
        this.mediaService
            .observe()
            .pipe(
                filter((changes: BreakpointState) => values(changes.breakpoints).length > 0),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.setMediaSize();
            });
    }

    get score(): SetGameScore {
        return this.scoreboard.score as SetGameScore;
    }

    scoreboardUpdate(): void {
        if (!this.isMobile) {
            if (this.score.participant1.sets.length <= this.desktopMaxInnings) {
                this.startingIndex = 0;
                this.endIndex = this.score.participant1.sets.length;
            } else if (this.score.participant1.sets.length >= this.desktopMaxInnings) {
                this.startingIndex = this.score.participant1.sets.length - this.desktopMaxInnings;
                this.endIndex = this.score.participant1.sets.length;
            }
        } else {
            if (this.score.participant1.sets.length >= this.defaultInnings) {
                this.startingIndex = this.score.participant1.sets.length - this.mobileMaxInnings;
                this.endIndex = this.score.participant1.sets.length;
            }
        }
        if (this.score.participant1.sets.length <= this.defaultInnings) {
            this.startingIndex = 0;
            this.mobileMaxInnings = this.defaultInnings;
            this.endIndex = this.defaultInnings;
        }
    }

    get currentInning(): number {
        return this.score ? this.score.participant1.sets.length - 1 : 0;
    }

    get innings(): string[] {
        this.player1runs = sumBy(this.score.participant1.sets, (set) => Number(set.points));
        this.player2runs = sumBy(this.score.participant2.sets, (set) => Number(set.points));
        let totalInnings = this.mobileMaxInnings;
        if (this.score.participant1.sets.length >= this.mobileMaxInnings) {
            totalInnings = this.score.participant1.sets.length;
        }

        return times(totalInnings).map((_, index) => `${index + 1}`);
    }

    private setMediaSize(): void {
        this.isMobile = this.mediaService.isActive('xs');
        if (this.isMobile) {
            this.mobileMaxInnings = this.defaultInnings;
        }
    }

    ngOnChanges(changes: ISimpleChanges<BaseballScoreboardLiveComponent>): void {
        if (changes.scoreboard) {
            this.mobileMaxInnings = this.maximumInningsInMobile;
            this.scoreboardUpdate();
            this.setInitialDisplayInningsIndex();
            const baseballStats = this.scoreboard.statistics as BaseballStatistics;
            this.homeTeamBase = this.baseballScoreboardFactory.getBaseIcon(baseballStats, OrderType.Team01);
            this.awayTeamBase = this.baseballScoreboardFactory.getBaseIcon(baseballStats, OrderType.Team02);
        }
    }

    scrollLeftDisplayInnings(event: Event): void {
        event.stopPropagation();
        this.startingIndex = 0;
        if (this.isMobile || this.isShowcase) {
            this.endIndex = this.mobileMaxInnings;
        } else {
            if (this.score.participant1.sets.length >= this.defaultInnings) {
                this.endIndex = this.desktopMaxInnings;
            }
        }
    }

    scrollRightDisplayInnings(event: Event): void {
        event.stopPropagation();
        this.setInitialDisplayInningsIndex();
    }

    setInitialDisplayInningsIndex(): void {
        if (this.isMobile || this.isShowcase) {
            this.startingIndex = this.innings.length - this.mobileMaxInnings;
            this.endIndex = this.innings.length;
        } else {
            if (this.innings.length >= this.desktopMaxInnings) {
                this.mobileMaxInnings = this.desktopMaxInnings;
                this.startingIndex = this.innings.length - this.mobileMaxInnings;
                this.endIndex = this.innings.length;
            } else {
                this.startingIndex = 0;
                this.endIndex = this.innings.length;
            }
        }
    }

    getPoints(participant: ParticipantSetScore, inning: number): number | string {
        return inning <= this.currentInning ? participant.sets[inning].points : '-';
    }

    getInningsClass(innings: number): string {
        if (this.isMobile) {
            return '';
        }
        let inningsClass = '';
        if (innings < 15) {
            inningsClass = 'dt-lt-15-inning-block';
        }

        return innings <= 20 ? inningsClass + ' ' + 'dt-lt-20-inning-block' : 'dt-gt-20-inning-block';
    }
}
